import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FaBars, FaTimes, FaPhone } from 'react-icons/fa';
import { MdOutlineMail } from 'react-icons/md';
import Button from '../common/Button.js';

const HeaderWrapper = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  transition: var(--transition);
  height: var(--header-height);
  background-color: var(--white);
  
  ${({ scrolled }) => scrolled && `
    box-shadow: var(--shadow-md);
  `}
  
  ${({ transparent, scrolled }) => transparent && !scrolled && `
    background-color: transparent;
    color: var(--white);
  `}
  
  @media (max-width: 768px) {
    height: var(--mobile-header-height);
  }
  
  .container {
    height: 100%;
    max-width: 100%; /* Allow full width */
    padding: 0; /* Override container padding */
    display: flex; /* Ensure flex display for proper height alignment */
  }
`;

const HeaderContainer = styled.div`
  display: grid;
  grid-template-columns: minmax(200px, auto) 1fr auto; /* Increased minimum width for logo */
  align-items: center;
  height: 100%;
  width: 100%;
  box-sizing: border-box;
  padding: 0; /* Remove padding - we'll control it in the sections */
  
  @media (max-width: 768px) {
    grid-template-columns: auto 1fr auto;
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0 1.5rem 0 2rem; /* Reduced right padding to give logo more space */
  
  @media (max-width: 768px) {
    padding: 0 0.75rem 0 1.5rem;
  }
`;

const CenterSection = styled.div`
  display: flex;
  justify-content: flex-start; /* Align nav links to start */
  padding-left: 2rem; /* Add space to the left */
  
  @media (max-width: 1024px) {
    display: none;
  }
  
  @media (min-width: 1280px) {
    justify-content: center; /* Center on larger screens */
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: 100%;
  padding: 0 2rem; /* Consistent padding with left section */
  
  @media (max-width: 768px) {
    padding: 0 1.5rem;
  }
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  height: 100%;
  margin-left: 0; /* Ensure no margin pushing it from edge */
  
  img {
    height: 65px; /* Increased height to fill the header better */
    max-width: 190px;
    object-fit: contain;
    transition: var(--transition);
    
    @media (max-width: 768px) {
      height: 45px; /* Increased but still smaller on mobile */
      max-width: 130px;
    }
  }
`;

const NavLinks = styled.nav`
  display: flex;
  gap: 3rem; /* Increased gap between nav links */
  padding-left: 0; /* Reset any default padding */
  font-size: 0.95rem;
  text-transform: uppercase;
  letter-spacing: 0.3px; /* Reduced from 0.5px for better readability */
`;

const NavLink = styled(Link)`
  color: ${({ transparent, scrolled }) => 
    transparent && !scrolled ? 'var(--white)' : 'var(--dark)'};
  font-weight: 600;
  position: relative;
  padding: 0.5rem 0;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 0;
    height: 3px;
    background-color: var(--secondary);
    transition: var(--transition);
  }
  
  &:hover {
    color: var(--secondary);
  }
  
  &:hover::after,
  &.active::after {
    width: 100%;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: var(--primary);
  font-size: 1.5rem;
  cursor: pointer;
  z-index: 1002; /* Ensure button is clickable */
  padding: 0.75rem;
  
  @media (max-width: 1024px) {
    display: block;
  }
  
  @media (max-width: 768px) {
    font-size: 1.25rem; /* Smaller icon on mobile */
  }
`;

const MobileMenu = styled.div`
  position: fixed;
  top: 0;
  right: ${({ isOpen }) => isOpen ? '0' : '-100%'};
  width: 280px; /* Slightly wider for better readability */
  height: 100vh;
  background-color: var(--white);
  z-index: 1001;
  padding: 2rem;
  box-shadow: var(--shadow-lg);
  transition: var(--transition);
  display: flex;
  flex-direction: column;
  overflow-y: auto; /* Add scrolling for smaller screens */
`;

const MobileNavLink = styled(Link)`
  margin: 1.25rem 0;
  color: var(--dark);
  font-weight: 500;
  font-size: 1.1rem; /* Slightly larger for better mobile tappability */
  position: relative;
  
  &.active, &:hover {
    color: var(--secondary);
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 0;
    height: 2px;
    background-color: var(--secondary);
    transition: var(--transition);
  }
  
  &:hover::after,
  &.active::after {
    width: 30%; /* Shorter underline for mobile */
  }
`;

const CloseButton = styled.button`
  align-self: flex-end;
  background: none;
  border: none;
  color: var(--dark);
  font-size: 1.5rem;
  cursor: pointer;
  margin-bottom: 2rem;
`;

const ContactInfo = styled.div`
  display: flex;
  align-items: center;
  
  @media (max-width: 1024px) {
    display: none;
  }
`;

const ContactItem = styled.a`
  display: flex;
  align-items: center;
  margin-left: 2rem; /* Increased margin for better spacing */
  color: ${({ transparent, scrolled }) => 
    transparent && !scrolled ? 'var(--white)' : 'var(--primary)'};
  font-size: 0.85rem; /* Smaller text for better fit */
  font-weight: 600;
  
  svg {
    margin-right: 0.5rem;
    color: var(--secondary);
    font-size: 1rem;
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: ${({ isOpen }) => isOpen ? 'block' : 'none'};
`;

const Header = ({ transparent = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  
  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };
  
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  
  return (
    <HeaderWrapper scrolled={scrolled} transparent={transparent}>
      <div className="container">
        <HeaderContainer>
          <LeftSection>
            <Logo to="/" transparent={transparent} scrolled={scrolled}>
              <img src="/images/shoffner-logo.png" alt="Shoffner Homes" />
            </Logo>
          </LeftSection>
          
          <CenterSection>
            <NavLinks>
              <NavLink to="/" transparent={transparent} scrolled={scrolled}>Home</NavLink>
              <NavLink to="/homes-for-sale" transparent={transparent} scrolled={scrolled}>Homes For Sale</NavLink>
              <NavLink to="/rental-properties" transparent={transparent} scrolled={scrolled}>Rental Properties</NavLink>
              <NavLink to="/land-development" transparent={transparent} scrolled={scrolled}>Land Development</NavLink>
              <NavLink to="/blog" transparent={transparent} scrolled={scrolled}>Blog</NavLink>
              <NavLink to="/contact" transparent={transparent} scrolled={scrolled}>Contact</NavLink>
            </NavLinks>
          </CenterSection>
          
          <RightSection>
            <ContactInfo>
              <ContactItem href="tel:3365166716" transparent={transparent} scrolled={scrolled}>
                <FaPhone /> (336) 516-6716
              </ContactItem>
              <ContactItem href="mailto:carl@shoffnerhomes.com?subject=Shoffner%20Homes%20Website%20Inquiry" transparent={transparent} scrolled={scrolled}>
                <MdOutlineMail /> carl@shoffnerhomes.com
              </ContactItem>
            </ContactInfo>
            
            <MobileMenuButton onClick={toggleMenu} transparent={transparent} scrolled={scrolled}>
              <FaBars />
            </MobileMenuButton>
          </RightSection>
        </HeaderContainer>
      </div>
      
      <MobileMenu isOpen={isOpen}>
        <CloseButton onClick={toggleMenu}>
          <FaTimes />
        </CloseButton>
        <MobileNavLink to="/" onClick={toggleMenu}>Home</MobileNavLink>
        <MobileNavLink to="/homes-for-sale" onClick={toggleMenu}>Homes For Sale</MobileNavLink>
        <MobileNavLink to="/rental-properties" onClick={toggleMenu}>Rental Properties</MobileNavLink>
        <MobileNavLink to="/land-development" onClick={toggleMenu}>Land Development</MobileNavLink>
        <MobileNavLink to="/blog" onClick={toggleMenu}>Blog</MobileNavLink>
        <MobileNavLink to="/contact" onClick={toggleMenu}>Contact</MobileNavLink>
        
        <div style={{ marginTop: 'auto' }}>
          <ContactItem href="tel:3365166716" transparent={false} scrolled={true} style={{ margin: '1rem 0' }}>
            <FaPhone /> (336) 516-6716
          </ContactItem>
          <ContactItem href="mailto:carl@shoffnerhomes.com?subject=Shoffner%20Homes%20Website%20Inquiry" transparent={false} scrolled={true} style={{ margin: '1rem 0' }}>
            <MdOutlineMail /> carl@shoffnerhomes.com
          </ContactItem>
          <Button as={Link} to="/contact" variant="secondary" fullWidth style={{ marginTop: '1rem' }}>
            Contact Us
          </Button>
        </div>
      </MobileMenu>
      
      <Overlay isOpen={isOpen} onClick={toggleMenu} />
    </HeaderWrapper>
  );
};

export default Header;